import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material'
import Tooltip from './ReportTooltip'
import ArticleOutlined from '@mui/icons-material/ArticleOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMapMarkedAlt, faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import DetailDataTable from '../../../components/table/DetailDataTable'
import TableExpandButton from '../../../components/table/TableExpandButton'
import { ListMagnifyingGlass, MapTrifold } from '@phosphor-icons/react'
import AppChip from '../../../components/chips/AppChip'
import MobileExpandableDetailCard from '../../../components/mobile/MobileExpandableDetailCard'
import { MobilePaginationBar } from '../../../components/mobile'
import { Table } from 'react-bootstrap'
import { MapModalProvider } from '../../hook/MapModalProvider'
import ActivityReportMapModal from './maps/ActivityReportMapModal'
import TimestampReportMapModal from './maps/TimestampReportMapModal'
import LoadingSpinner from '../../../components/LoadingSpinner'
import MobileEmptyStateCard from '../../../components/MobileEmptyStateCard'
import Pagination from '../../../layout/Pagination'
import MuiPagination from '@mui/material/Pagination'
import UbiquaTablePagination from '../../../components/table/UbiquaTablePagination'
import { formatMoney } from '../../../helpers/utils'
import { REPORT_PRIMARY_ACCENT } from '../../constants/reportUiTokens'
import { REPORT_MOBILE_OUTLINED_ACTION_BUTTON_SX } from '../mobile/styles/reportMobileActionButtonSx'
import {
  buildActivityDialogState,
  buildTimestampDialogState,
  CENTER_ALIGNED_HEADERS,
  DETAIL_HEADERS,
  EMPTY_DIALOG_STATE,
  formatDateTimeLabel,
  MAIN_HEADERS_BY_VARIANT
} from '../helpers/sharedVisitsTable'
import { getGeofenceStatus } from '../helpers/geofenceStatus'
import { getVisitStatusChipVariant, VISIT_ACTIVITY_CHIP_VARIANT } from '../helpers/visitChipVariant'

const renderRouteMobileActivityChips = (row) =>
  row.activities?.length ? (
    <Stack
      direction="row"
      useFlexGap
      flexWrap="wrap"
      alignItems="center"
      gap={0.75}
      sx={{ mt: 0.25 }}
    >
      {row.activities.map((activity, index) => (
        <AppChip
          key={`${row.rowKey}-mobile-activity-${activity}-${index}`}
          label={activity}
          variant={VISIT_ACTIVITY_CHIP_VARIANT}
        />
      ))}
    </Stack>
  ) : (
    '-'
  )

const renderVisitStatusChip = (row) => (
  <AppChip
    label={row.statusLabel || '-'}
    variant={row.statusVariant || getVisitStatusChipVariant(row.statusClassName)}
    className={row.statusChipClassName}
  />
)

const renderBooleanStatus = (value, className) => {
  const normalizedValue = value || '-'
  if (normalizedValue === '-') return '-'

  return <AppChip {...getGeofenceStatus(normalizedValue)} className={className} />
}

const buildVisitMobileSummaryRows = (row, variant, { expanded } = {}) => {
  const isRoute = variant === 'route'

  const activitiesValue = isRoute ? (
    renderRouteMobileActivityChips(row)
  ) : row.activities?.length ? (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        flexWrap: 'wrap',
        gap: 0.75,
        mt: 0.5,
        verticalAlign: 'top'
      }}
    >
      {row.activities.map((activity, index) => (
        <AppChip
          key={`${row.rowKey}-mobile-activity-${activity}-${index}`}
          label={activity}
          variant={VISIT_ACTIVITY_CHIP_VARIANT}
        />
      ))}
    </Box>
  ) : (
    '-'
  )

  const statusValue = renderVisitStatusChip(row)

  const list = [
    { label: 'Fecha planificada', value: row.plannedDateLabel || '-' },
    { label: 'Fecha de ejecución', value: row.executedDateLabel || '-' },
    { label: 'Duración', value: row.visitDuration || '-' }
  ]

  if (isRoute) {
    list.push({
      label: 'Dentro de ruta',
      value: renderBooleanStatus(row.inRouteLabel, 'route-boolean-status-chip'),
      alignLabelAndValue: true
    })
  }

  list.push({
    label: 'Efectividad',
    value: renderBooleanStatus(
      row.effectivenessLabel,
      isRoute ? 'route-boolean-status-chip' : undefined
    ),
    alignLabelAndValue: isRoute
  })

  const activitiesRow = {
    label: 'Actividades',
    value: activitiesValue,
    ...(isRoute ? { alignLabelAndValue: true } : {})
  }

  if (isRoute && expanded) {
    list.push(activitiesRow)
    return list
  }

  const estadoRow = {
    label: 'Estado',
    value: statusValue,
    ...(isRoute ? { alignLabelAndValue: true } : {})
  }

  list.push(activitiesRow, estadoRow)

  return list
}

const buildRouteMobileEstadoDetailRow = (row) => ({
  detailRowKey: `${row.rowKey}-route-estado`,
  label: 'Estado',
  alignLabelAndValue: true,
  value: (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      {renderVisitStatusChip(row)}
    </Box>
  )
})

const appendRouteMobileEstadoRow = (rows, row, variant) => {
  if (variant !== 'route' || !row) return rows
  return [...rows, buildRouteMobileEstadoDetailRow(row)]
}

const buildVisitMobileDetailRows = (
  detailRows,
  row,
  renderActivityCode,
  handleOpenLocation,
  variant = 'unified'
) =>
  (detailRows || []).map((detailRow, idx) => {
    const mapButton = (
      <Tooltip title="Ver ubicación">
        <span style={{ display: 'block', width: '100%' }}>
          <Button
            variant="outlined"
            size="small"
            fullWidth
            startIcon={<LocationOnOutlinedIcon fontSize="small" />}
            disabled={!detailRow.mapPayload}
            onClick={() => handleOpenLocation(detailRow, row.clientName)}
            sx={{
              ...REPORT_MOBILE_OUTLINED_ACTION_BUTTON_SX,
              '&:hover': {
                borderColor: '#597fa9',
                bgcolor: '#597fa9',
                color: '#FFFFFF'
              }
            }}
          >
            Ver ubicación
          </Button>
        </span>
      </Tooltip>
    )

    const fieldsStack = (
      <Stack spacing={0.75} sx={{ pt: 0.25 }}>
        <Typography variant="body2" component="div">
          Código: {renderActivityCode(detailRow)}
        </Typography>
        <Typography variant="body2" component="div">
          Actividad: {detailRow.label || '-'}
        </Typography>
        <Typography variant="body2" component="div">
          Motivo / descripción: {detailRow.description || '-'}
        </Typography>
        <Typography variant="body2" component="div">
          Monto: {detailRow.amount === null ? '-' : formatMoney(detailRow.amount)}
        </Typography>
        <Typography variant="body2" component="div">
          Dentro de geocerca: {detailRow.inGeofence || 'N/A'}
        </Typography>
        {variant === 'route' ? null : <Box sx={{ pt: 0.5 }}>{mapButton}</Box>}
      </Stack>
    )

    return {
      icon: <ArticleOutlined fontSize="small" />,
      label: `Registro ${idx + 1} · ${formatDateTimeLabel(detailRow.datetime)}`,
      value: fieldsStack,
      ...(variant === 'route' ? { fullWidthFooter: mapButton } : {})
    }
  })

const SharedVisitsTable = ({
  variant = 'unified',
  rows = [],
  isLoading = false,
  currentPage,
  totalPages,
  onPaginate,
  onFetchRowDetails,
  salesmanName,
  formatActivityName,
  isMobileLayout = false,
  hidePagination = false,
  onPageChange,
  initialExpandedRowKey = null,
  towerLayout = false,
  totalCount = 0,
  perPage = 25,
  onRowsPerPageChange,
  paginationItemLabel = 'registros',
  showPageSizeControl = false
}) => {
  const navigate = useNavigate()
  const [expandedRowKey, setExpandedRowKey] = useState(initialExpandedRowKey)
  const [detailRowsByKey, setDetailRowsByKey] = useState({})
  const [detailLoadingByKey, setDetailLoadingByKey] = useState({})
  const [dialogState, setDialogState] = useState(EMPTY_DIALOG_STATE)
  const [shouldRenderMobileMap, setShouldRenderMobileMap] = useState(false)
  const activeDetailRequestRef = useRef(0)

  useEffect(() => {
    activeDetailRequestRef.current += 1
    setExpandedRowKey(initialExpandedRowKey)
    setDetailRowsByKey({})
    setDetailLoadingByKey({})
  }, [currentPage, rows, variant, isMobileLayout, initialExpandedRowKey])

  const mainHeaders = useMemo(
    () => MAIN_HEADERS_BY_VARIANT[variant] || MAIN_HEADERS_BY_VARIANT.unified,
    [variant]
  )

  useEffect(() => {
    if (!dialogState.open || !dialogState.payload) {
      setShouldRenderMobileMap(false)
      return
    }

    if (!isMobileLayout) {
      setShouldRenderMobileMap(true)
      return
    }

    setShouldRenderMobileMap(false)
    let animationFrameId = 0
    let nestedAnimationFrameId = 0

    animationFrameId = window.requestAnimationFrame(() => {
      nestedAnimationFrameId = window.requestAnimationFrame(() => {
        setShouldRenderMobileMap(true)
      })
    })

    return () => {
      window.cancelAnimationFrame(animationFrameId)
      window.cancelAnimationFrame(nestedAnimationFrameId)
    }
  }, [dialogState.open, dialogState.payload, isMobileLayout])

  const handleCloseDialog = () => {
    setShouldRenderMobileMap(false)
    setDialogState(EMPTY_DIALOG_STATE)
  }

  const handleMapModalExit = () => {
    setShouldRenderMobileMap(false)
  }

  const openTimestampDialog = (model, fallbackName, detailRow = null) => {
    setDialogState(
      buildTimestampDialogState({
        model,
        fallbackName,
        detailRow,
        salesmanName
      })
    )
  }

  const openActivityDialog = (model, fallbackName) => {
    setDialogState(
      buildActivityDialogState({
        model,
        fallbackName,
        salesmanName,
        formatActivityName
      })
    )
  }

  const handleOpenLocation = (detailRow, fallbackName) => {
    const mapPayload = detailRow?.mapPayload
    if (!mapPayload?.model) return

    if (mapPayload.kind === 'timestamp') {
      openTimestampDialog(mapPayload.model, fallbackName, detailRow)
      return
    }

    openActivityDialog(mapPayload.model, fallbackName)
  }

  const renderActivityCode = (detailRow) => {
    if (!detailRow.activityUrl) {
      return detailRow.activityCode || '-'
    }

    return (
      <a
        href={detailRow.activityUrl}
        onClick={(event) => {
          event.preventDefault()
          navigate(detailRow.activityUrl)
        }}
        className="report-activity-link"
        style={{
          color: REPORT_PRIMARY_ACCENT,
          textDecoration: 'underline',
          textUnderlineOffset: '2px',
          cursor: 'pointer'
        }}
      >
        {detailRow.activityCode || '-'}
      </a>
    )
  }

  const buildDetailColumns = (row) => {
    const definitions = [
      { key: 'datetime', render: (detail) => formatDateTimeLabel(detail.datetime) },
      { key: 'activityCode', render: renderActivityCode },
      {
        key: 'label',
        render: (detail) => (
          <AppChip label={detail.label || '-'} variant={VISIT_ACTIVITY_CHIP_VARIANT} />
        )
      },
      { key: 'description', render: (detail) => detail.description || '-' },
      {
        key: 'amount',
        render: (detail) => (detail.amount === null ? '-' : formatMoney(detail.amount))
      },
      { key: 'inGeofence', render: (detail) => detail.inGeofence || 'N/A' },
      {
        key: 'location',
        render: (detail) => (
          <Tooltip
            title={detail.mapPayload ? 'Ver ubicación' : 'Ubicación no disponible'}
            placement="top"
            arrow
          >
            <span>
              <button
                type="button"
                disabled={!detail.mapPayload}
                aria-label={detail.mapPayload ? 'Ver ubicación' : 'Ubicación no disponible'}
                onClick={() => handleOpenLocation(detail, row.clientName)}
                className={
                  variant === 'route' ? 'route-location-action' : 'report-maps-activity-button'
                }
              >
                {variant === 'route' ? (
                  <MapTrifold size={20} weight="regular" aria-hidden="true" />
                ) : (
                  <FontAwesomeIcon icon={faMapMarkedAlt} />
                )}
              </button>
            </span>
          </Tooltip>
        )
      }
    ]
    return definitions.map((column, index) => {
      const header = DETAIL_HEADERS[index]
      return {
        ...column,
        label: header.label,
        align: header.justifyHeader ? 'center' : 'left',
        headerRender: () => (
          <Tooltip title={header.description} placement="top" arrow>
            <div
              className={`table-details-header-container ${header.justifyHeader ? 'justify-content-center' : ''}`}
            >
              <span className="th-header-text">{header.label.toUpperCase()}</span>
              <FontAwesomeIcon icon={faQuestionCircle} className="table-header-help-icon" />
            </div>
          </Tooltip>
        )
      }
    })
  }

  const mobileEmptyState =
    variant === 'route' ? (
      <MobileEmptyStateCard
        icon={<LocationOnOutlinedIcon fontSize="small" />}
        title="No hay visitas registradas"
        description="Cuando existan visitas en el rango seleccionado, aparecerán aquí con su detalle."
      />
    ) : (
      <MobileEmptyStateCard
        icon={<ArticleOutlined fontSize="small" />}
        title="No hay registros disponibles"
        description="Cuando existan registros en el rango seleccionado, aparecerán aquí con su detalle."
      />
    )

  const ensureRowDetailsLoaded = async (row) => {
    if (!row.hasDetail || !onFetchRowDetails) {
      return
    }

    if (Object.prototype.hasOwnProperty.call(detailRowsByKey, row.rowKey)) {
      return
    }

    activeDetailRequestRef.current += 1
    const requestId = activeDetailRequestRef.current

    setDetailLoadingByKey((prevState) => ({
      ...prevState,
      [row.rowKey]: true
    }))

    try {
      const details = await onFetchRowDetails(row.rawRow || row)
      if (activeDetailRequestRef.current !== requestId) return

      setDetailRowsByKey((prevState) => ({
        ...prevState,
        [row.rowKey]: details || []
      }))
    } catch (error) {
      console.error('Error loading visit row details:', error)
      if (activeDetailRequestRef.current !== requestId) return

      setDetailRowsByKey((prevState) => ({
        ...prevState,
        [row.rowKey]: []
      }))
    } finally {
      if (activeDetailRequestRef.current !== requestId) return

      setDetailLoadingByKey((prevState) => ({
        ...prevState,
        [row.rowKey]: false
      }))
    }
  }

  useEffect(() => {
    if (!initialExpandedRowKey || !rows.length) return

    const initialRow = rows.find((row) => row.rowKey === initialExpandedRowKey)
    if (!initialRow) return

    setExpandedRowKey(initialRow.rowKey)
    void ensureRowDetailsLoaded(initialRow)
  }, [initialExpandedRowKey, rows])

  const handleToggleRow = async (row) => {
    if (expandedRowKey === row.rowKey) {
      activeDetailRequestRef.current += 1
      setExpandedRowKey(null)
      return
    }

    setExpandedRowKey(row.rowKey)
    await ensureRowDetailsLoaded(row)
  }

  const mobileList = isMobileLayout ? (
    <Stack spacing={1.5} sx={{ width: '100%', minWidth: 0 }}>
      {isLoading ? (
        <Stack spacing={1.5} aria-busy="true">
          <span role="status" className="visually-hidden">
            Cargando visitas
          </span>
          {Array.from({ length: rows.length || perPage }, (_, index) => (
            <Box
              key={index}
              aria-hidden="true"
              sx={{ p: 2, border: '1px solid #edf1f6', borderRadius: 2 }}
            >
              <Skeleton animation="wave" variant="rounded" width="68%" height={14} />
              <Skeleton animation="wave" variant="rounded" width="40%" height={10} sx={{ mt: 1 }} />
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Skeleton animation="wave" variant="rounded" width="32%" height={12} />
                <Skeleton animation="wave" variant="rounded" width="24%" height={20} />
              </Stack>
            </Box>
          ))}
        </Stack>
      ) : rows.length === 0 ? (
        mobileEmptyState
      ) : (
        rows.map((row) => {
          const isExpanded = expandedRowKey === row.rowKey
          const detailRows = detailRowsByKey[row.rowKey] || []
          const isDetailLoading = Boolean(detailLoadingByKey[row.rowKey])
          const hasCachedDetail = Object.prototype.hasOwnProperty.call(detailRowsByKey, row.rowKey)

          let mobileDetailRows = []
          if (isExpanded && row.hasDetail) {
            if (isDetailLoading) {
              mobileDetailRows = appendRouteMobileEstadoRow(
                [
                  {
                    detailRowKey: `${row.rowKey}-loading`,
                    icon: <ArticleOutlined fontSize="small" />,
                    label: 'Cargando',
                    value: (
                      <Box sx={{ py: 1 }}>
                        <LoadingSpinner text="Cargando..." />
                      </Box>
                    )
                  }
                ],
                row,
                variant
              )
            } else if (hasCachedDetail && detailRows.length > 0) {
              mobileDetailRows = appendRouteMobileEstadoRow(
                buildVisitMobileDetailRows(
                  detailRows,
                  row,
                  renderActivityCode,
                  handleOpenLocation,
                  variant
                ),
                row,
                variant
              )
            } else {
              mobileDetailRows = appendRouteMobileEstadoRow(
                [
                  {
                    detailRowKey: `${row.rowKey}-no-detail`,
                    icon: <ArticleOutlined fontSize="small" />,
                    label: 'Detalle',
                    value: 'Sin detalle disponible para esta visita'
                  }
                ],
                row,
                variant
              )
            }
          }

          return (
            <MobileExpandableDetailCard
              key={row.rowKey}
              code={row.clientLabel || '-'}
              expanded={isExpanded}
              onToggleExpand={() => handleToggleRow(row)}
              summaryRows={buildVisitMobileSummaryRows(row, variant, { expanded: isExpanded })}
              detailRows={mobileDetailRows}
              showExpandControl={Boolean(row.hasDetail)}
              lighterDetailDividers={variant === 'route'}
              bodyFontSize={variant === 'route' ? 14 : undefined}
              codeFontSize={variant === 'route' ? 14 : undefined}
              summaryLabelFontSize={variant === 'route' ? 14 : undefined}
              summaryValueFontSize={variant === 'route' ? 14 : undefined}
              detailLabelFontSize={variant === 'route' ? 14 : undefined}
              detailValueFontSize={variant === 'route' ? 14 : undefined}
            />
          )
        })
      )}
      {!hidePagination && totalPages > 1 ? (
        <Box sx={{ mt: 1 }}>
          <MobilePaginationBar
            current_page={currentPage}
            total_pages={totalPages}
            disablePaper
            handle_pagination={(event) => {
              setExpandedRowKey(null)
              onPaginate?.(event)
            }}
          />
        </Box>
      ) : null}
    </Stack>
  ) : null

  const useTowerPagination =
    towerLayout && variant === 'route' && !isMobileLayout && !hidePagination && onPageChange

  const paginationTotalCount = Math.max(0, Number(totalCount) || rows.length)
  const shouldShowTowerPagination = useTowerPagination && paginationTotalCount > 10

  const towerPaginationNode = shouldShowTowerPagination ? (
    <UbiquaTablePagination
      ariaLabel="Paginación de visitas"
      itemLabel={paginationItemLabel}
      emptyLabel="Sin visitas para mostrar"
      showPageSizeControl={showPageSizeControl}
      disabled={isLoading}
      pagination={{
        total_count: paginationTotalCount,
        per_page: perPage,
        total_pages: Math.max(1, totalPages || 1),
        current_page: currentPage
      }}
      onPageChange={(_, page) => {
        setExpandedRowKey(null)
        onPageChange(page)
      }}
      onRowsPerPageChange={onRowsPerPageChange}
    />
  ) : null

  const towerEmptyState = (
    <Box
      sx={{
        minHeight: 220,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 3,
        py: 4
      }}
    >
      <Box sx={{ maxWidth: 360 }}>
        <Box
          sx={{
            width: 66,
            height: 66,
            borderRadius: '50%',
            backgroundColor: '#F2F6FB',
            color: '#8FA4BC',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1.75
          }}
        >
          <ListMagnifyingGlass size={34} weight="regular" />
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1A2733', mb: 1 }}>
          No hay visitas para mostrar
        </Typography>
        <Typography sx={{ fontSize: 12, lineHeight: 1.55, color: '#6B7A8C' }}>
          No encontramos visitas con los filtros actuales. Prueba ajustando la búsqueda o
          restableciendo los filtros.
        </Typography>
      </Box>
    </Box>
  )

  const desktopTable = (
    <>
      <div
        className={towerLayout ? 'route-tower-table-card ubiqua-data-table-card' : undefined}
        aria-busy={isLoading}
        style={{ position: 'relative' }}
      >
        {isLoading ? (
          <span role="status" className="visually-hidden">
            Cargando visitas
          </span>
        ) : null}
        <div className={towerLayout ? 'route-tower-scroll ubiqua-data-table-scroll' : undefined}>
          <Table
            hover
            responsive
            className={towerLayout ? 'route-tower-table ubiqua-data-table' : undefined}
          >
            <thead className="table-header-gray-mui ubiqua-data-table__head">
              <tr>
                {mainHeaders.map((header) => (
                  <th
                    key={header.label}
                    className={
                      CENTER_ALIGNED_HEADERS.has(header.label) ? 'align-center-table-column' : ''
                    }
                  >
                    <Tooltip title={header.description} placement="top" arrow>
                      <div
                        className={`table-details-header-container ${
                          CENTER_ALIGNED_HEADERS.has(header.label) ? 'justify-content-center' : ''
                        }`}
                      >
                        <span className="th-header-text">{header.label.toUpperCase()}</span>
                        <FontAwesomeIcon
                          icon={faQuestionCircle}
                          className="table-header-help-icon"
                        />
                      </div>
                    </Tooltip>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: rows.length || perPage }, (_, rowIndex) => (
                  <tr key={`skeleton-${rowIndex}`} aria-hidden="true">
                    {mainHeaders.map((header, columnIndex) => {
                      const isExpand = header.label === 'Ampliar'
                      const isClient = header.label === 'Cliente'
                      const isCentered = CENTER_ALIGNED_HEADERS.has(header.label)
                      const width = [72, 86, 64, 80][(rowIndex + columnIndex) % 4]
                      return (
                        <td key={header.label} style={{ height: 58 }}>
                          <Skeleton
                            animation="wave"
                            variant={isExpand ? 'circular' : 'rounded'}
                            width={isExpand ? 18 : isCentered ? 58 : `${width}%`}
                            height={isExpand || isCentered ? 18 : 12}
                            sx={{
                              borderRadius: isCentered ? '999px' : undefined,
                              mx: isCentered ? 'auto' : 0
                            }}
                          />
                          {isClient ? (
                            <Skeleton
                              animation="wave"
                              variant="rounded"
                              width="48%"
                              height={9}
                              sx={{ mt: 0.75 }}
                            />
                          ) : null}
                        </td>
                      )
                    })}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr className="route-tower-empty-row">
                  <td colSpan={mainHeaders.length} style={{ padding: 0 }}>
                    {towerLayout ? towerEmptyState : 'No hay resultados'}
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const isExpanded = expandedRowKey === row.rowKey
                  const detailRows = detailRowsByKey[row.rowKey] || []
                  const isDetailLoading = Boolean(detailLoadingByKey[row.rowKey])
                  const hasCachedDetail = Object.prototype.hasOwnProperty.call(
                    detailRowsByKey,
                    row.rowKey
                  )

                  return (
                    <Fragment key={row.rowKey}>
                      <tr>
                        <td className="align-center-table-column">
                          {row.hasDetail ? (
                            <TableExpandButton
                              expanded={isExpanded}
                              label={
                                isDetailLoading
                                  ? 'Cargando detalle'
                                  : isExpanded
                                    ? 'Ocultar detalle'
                                    : 'Mostrar detalle'
                              }
                              onClick={() => handleToggleRow(row)}
                            />
                          ) : null}
                        </td>
                        <td className="td-text-limited">
                          {variant === 'route' && row.clientName && row.rawRow?.client_uid ? (
                            <Tooltip title={row.clientLabel || '-'} placement="top" arrow>
                              <span className="route-client-cell ubiqua-identity-stacked">
                                <span className="route-client-name ubiqua-identity-stacked__name">
                                  {row.clientName}
                                </span>
                                <span className="route-client-code ubiqua-identity-stacked__code">
                                  {row.rawRow.client_uid}
                                </span>
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title={row.clientLabel || '-'} placement="top" arrow>
                              <span className="route-client-name">{row.clientLabel || '-'}</span>
                            </Tooltip>
                          )}
                        </td>
                        <td className="td-text-limited route-cell-tabular">
                          {row.plannedDateLabel || '-'}
                        </td>
                        <td className="td-text-limited route-cell-tabular">
                          {row.executedDateLabel || '-'}
                        </td>
                        <td className="td-text-limited route-cell-tabular">
                          {row.visitDuration || '-'}
                        </td>
                        {variant === 'route' ? (
                          <td className="td-text-limited align-center-table-column route-cell-bool">
                            {renderBooleanStatus(row.inRouteLabel, 'route-boolean-status-chip')}
                          </td>
                        ) : null}
                        <td className="td-text-limited align-center-table-column route-cell-bool">
                          {renderBooleanStatus(
                            row.effectivenessLabel,
                            variant === 'route' ? 'route-boolean-status-chip' : undefined
                          )}
                        </td>
                        <td className="td-text-limited">
                          {row.activities?.length ? (
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {row.activities.map((activity, index) => (
                                <AppChip
                                  key={`${row.rowKey}-${activity}-${index}`}
                                  label={activity}
                                  variant={VISIT_ACTIVITY_CHIP_VARIANT}
                                />
                              ))}
                            </Box>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="td-text-limited align-center-table-column">
                          <AppChip
                            label={row.statusLabel || '-'}
                            variant={
                              row.statusVariant || getVisitStatusChipVariant(row.statusClassName)
                            }
                            className={row.statusChipClassName}
                          />
                        </td>
                      </tr>

                      {isExpanded ? (
                        <tr className="route-expanded-detail-row">
                          <td
                            aria-hidden="true"
                            style={{
                              padding: 0,
                              borderTop: 'none',
                              backgroundColor: '#F7F7F7',
                              '--bs-table-accent-bg': '#F7F7F7'
                            }}
                          />
                          <td
                            colSpan={mainHeaders.length - 1}
                            style={{
                              padding: 0,
                              borderTop: 'none'
                            }}
                          >
                            <Box
                              sx={{
                                p: 2.5,
                                ...(variant === 'route' ? { pl: 0 } : {}),
                                bgcolor: '#F7F7F7'
                              }}
                            >
                              <DetailDataTable
                                ariaLabel={`Detalle de visita de ${row.clientName || row.clientLabel || 'cliente'}`}
                                columns={buildDetailColumns(row)}
                                rows={hasCachedDetail ? detailRows : []}
                                loading={isDetailLoading}
                                minWidth={1050}
                                loadingContent={<LoadingSpinner text="Cargando..." />}
                                emptyContent={
                                  <Box sx={{ py: 3, textAlign: 'center' }}>
                                    Sin detalle disponible para esta visita
                                  </Box>
                                }
                              />
                            </Box>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  )
                })
              )}
            </tbody>
          </Table>
        </div>
      </div>
      {towerPaginationNode}
    </>
  )

  return (
    <MapModalProvider>
      <>
        {isMobileLayout ? mobileList : desktopTable}

        {!isMobileLayout &&
        !towerLayout &&
        !hidePagination &&
        variant === 'route' &&
        onPageChange ? (
          <footer className="rp-pagination">
            <span className="rp-page-size">{rows.length} visitas en esta página</span>
            <span className="rp-row-count">
              Página {currentPage} de {Math.max(1, totalPages || 0)}
            </span>
            <MuiPagination
              count={Math.max(1, totalPages || 0)}
              page={currentPage}
              size="small"
              shape="rounded"
              disabled={isLoading}
              onChange={(_, page) => {
                setExpandedRowKey(null)
                onPageChange(page)
              }}
              getItemAriaLabel={(type, page) =>
                type === 'page'
                  ? `Ir a la página ${page}`
                  : type === 'next'
                    ? 'Página siguiente'
                    : 'Página anterior'
              }
            />
          </footer>
        ) : !isMobileLayout && !useTowerPagination && !hidePagination && totalPages > 1 ? (
          <Box sx={{ mt: 2.5 }}>
            <Pagination
              current_page={currentPage}
              total_pages={totalPages}
              handle_pagination={(event) => {
                setExpandedRowKey(null)
                onPaginate?.(event)
              }}
            />
          </Box>
        ) : null}

        <ActivityReportMapModal
          show={dialogState.open && dialogState.type === 'activity' && Boolean(dialogState.payload)}
          onHide={handleCloseDialog}
          isMobileLayout={isMobileLayout}
          salesmanName={salesmanName}
          activity={dialogState.type === 'activity' ? dialogState.payload : null}
          formatActivityName={formatActivityName}
          shouldRenderMobileMap={shouldRenderMobileMap}
        />

        <TimestampReportMapModal
          show={
            dialogState.open && dialogState.type === 'timestamp' && Boolean(dialogState.payload)
          }
          onHide={handleCloseDialog}
          onExit={handleMapModalExit}
          isMobileLayout={isMobileLayout}
          salesmanName={salesmanName}
          timestamp={dialogState.type === 'timestamp' ? dialogState.payload : null}
          shouldRenderMobileMap={shouldRenderMobileMap}
          detailsTitle="Detalles de captura de cliente"
          mobileEyebrow={dialogState.mobileDetails?.eyebrow || 'Detalle de horario'}
          activityName={dialogState.payload?.capture_activity_name || 'Control de horario'}
        />
      </>
    </MapModalProvider>
  )
}

export default SharedVisitsTable
